// =============================================================================
// CROSSROADS RMC — Custom JavaScript
// =============================================================================

( function () {
	'use strict';

	// -------------------------------------------------------------------------
	// Navbar: scroll state + hero offset
	// -------------------------------------------------------------------------
	const header = document.getElementById( 'site-header' );
	if ( ! header ) return;

	/**
	 * Write the current navbar height as a CSS custom property on :root.
	 * The SCSS consumes --navbar-height for page padding and hero offset.
	 */
	function setNavbarHeight() {
		document.documentElement.style.setProperty(
			'--navbar-height',
			header.offsetHeight + 'px'
		);
	}

	/**
	 * Toggle .is-scrolled once the user has scrolled past the navbar height.
	 * CSS handles the transparent → white transition via this class.
	 */
	function handleScroll() {
		header.classList.toggle( 'is-scrolled', window.scrollY >= header.offsetHeight );
	}

	/**
	 * If the element immediately after the header is a hero (.is-hero),
	 * pull it up behind the fixed navbar and push its inner content below it.
	 * Runs on init and on resize to stay in sync with a variable navbar height.
	 */
	function syncHeroOffset() {
		const next = header.nextElementSibling;
		if ( ! next || ! next.classList.contains( 'is-hero' ) ) return;

		const h = header.offsetHeight;
		next.style.marginTop = '-' + h + 'px';

		const inner = next.querySelector( '.is-hero__inner' );
		if ( inner ) {
			inner.style.paddingTop = h + 'px';
		}
	}

	// Init
	setNavbarHeight();
	handleScroll();
	syncHeroOffset();

	// Scroll listener (passive for performance)
	window.addEventListener( 'scroll', handleScroll, { passive: true } );

	// Resize: debounced via rAF so we don't thrash layout on every pixel
	let rafId;
	window.addEventListener( 'resize', function () {
		cancelAnimationFrame( rafId );
		rafId = requestAnimationFrame( function () {
			setNavbarHeight();
			syncHeroOffset();
		} );
	} );

} )();


// =============================================================================
// Mega-menu nav
// =============================================================================

( function () {
	'use strict';

	var header   = document.getElementById( 'site-header' );
	if ( ! header ) return;

	var triggers      = Array.from( header.querySelectorAll( '.nav-mega-trigger' ) );
	var panels        = Array.from( header.querySelectorAll( '.nav-mega-panel' ) );
	var panelsWrap    = document.getElementById( 'nav-mega-panels' );
	var closeTimer    = null;

	function openPanel( panelId ) {
		clearTimeout( closeTimer );
		triggers.forEach( function ( t ) {
			var active = t.dataset.panel === panelId;
			t.setAttribute( 'aria-expanded', active ? 'true' : 'false' );
			t.classList.toggle( 'is-active', active );
		} );
		panels.forEach( function ( p ) {
			p.classList.toggle( 'is-open', p.id === 'nav-panel-' + panelId );
		} );
		if ( panelsWrap ) panelsWrap.classList.add( 'has-open' );
	}

	function closeAll() {
		triggers.forEach( function ( t ) {
			t.setAttribute( 'aria-expanded', 'false' );
			t.classList.remove( 'is-active' );
		} );
		panels.forEach( function ( p ) { p.classList.remove( 'is-open' ); } );
		if ( panelsWrap ) panelsWrap.classList.remove( 'has-open' );
	}

	function scheduleClose() {
		closeTimer = setTimeout( closeAll, 350 );
	}

	// Desktop: hover opens, click toggles
	triggers.forEach( function ( trigger ) {
		trigger.addEventListener( 'mouseenter', function () {
			openPanel( trigger.dataset.panel );
		} );
		trigger.addEventListener( 'mouseleave', scheduleClose );
		trigger.addEventListener( 'click', function () {
			if ( trigger.classList.contains( 'is-active' ) ) {
				closeAll();
			} else {
				openPanel( trigger.dataset.panel );
			}
		} );
	} );

	panels.forEach( function ( panel ) {
		panel.addEventListener( 'mouseenter', function () { clearTimeout( closeTimer ); } );
		panel.addEventListener( 'mouseleave', scheduleClose );
	} );

	// Close on Escape or outside click
	document.addEventListener( 'keydown', function ( e ) {
		if ( e.key === 'Escape' ) closeAll();
	} );
	document.addEventListener( 'click', function ( e ) {
		if ( ! header.contains( e.target ) ) closeAll();
	} );

	// ── Mobile drawer ────────────────────────────────────────────────────

	var mobileToggle = header.querySelector( '.nav-mobile-toggle' );
	var mobileClose  = header.querySelector( '.nav-mobile-close' );
	var drawer       = document.getElementById( 'nav-drawer' );

	function openDrawer() {
		if ( ! drawer ) return;
		drawer.hidden = false;
		document.body.classList.add( 'nav-drawer-open' );
		if ( mobileToggle ) mobileToggle.setAttribute( 'aria-expanded', 'true' );
		drawer.focus();
	}

	function closeDrawer() {
		if ( ! drawer ) return;
		drawer.hidden = true;
		document.body.classList.remove( 'nav-drawer-open' );
		if ( mobileToggle ) mobileToggle.setAttribute( 'aria-expanded', 'false' );
	}

	if ( mobileToggle ) mobileToggle.addEventListener( 'click', openDrawer );
	if ( mobileClose )  mobileClose.addEventListener( 'click', closeDrawer );

	// ── Mobile accordion ─────────────────────────────────────────────────

	if ( drawer ) {
		var accordionTriggers = Array.from( drawer.querySelectorAll( '.nav-mobile-item__trigger' ) );

		accordionTriggers.forEach( function ( btn ) {
			btn.addEventListener( 'click', function () {
				var item   = btn.closest( '.nav-mobile-item--dropdown' );
				var isOpen = item.classList.contains( 'is-open' );

				// Close all others
				accordionTriggers.forEach( function ( other ) {
					var otherItem = other.closest( '.nav-mobile-item--dropdown' );
					otherItem.classList.remove( 'is-open' );
					other.setAttribute( 'aria-expanded', 'false' );
					other.classList.remove( 'is-active' );
				} );

				if ( ! isOpen ) {
					item.classList.add( 'is-open' );
					btn.setAttribute( 'aria-expanded', 'true' );
					btn.classList.add( 'is-active' );
				}
			} );
		} );
	}

} )();


// =============================================================================
// Section entrance animations
// Watches [data-rmc-animate] elements; adds the animation class when the
// element enters the viewport. Requires IntersectionObserver (all modern browsers).
// =============================================================================

( function () {
	'use strict';

	if ( ! ( 'IntersectionObserver' in window ) ) {
		// Graceful degradation: just make all sections visible immediately
		document.querySelectorAll( '[data-rmc-animate]' ).forEach( function ( el ) {
			el.style.opacity = '1';
		} );
		return;
	}

	var observer = new IntersectionObserver(
		function ( entries ) {
			entries.forEach( function ( entry ) {
				if ( ! entry.isIntersecting ) return;
				var el  = entry.target;
				var cls = el.getAttribute( 'data-rmc-animate' );
				if ( cls ) {
					el.classList.add( cls );
				}
				observer.unobserve( el );
			} );
		},
		{ threshold: 0.12 }
	);

	document.querySelectorAll( '[data-rmc-animate]' ).forEach( function ( el ) {
		observer.observe( el );
	} );

} )();


// =============================================================================
// Benefits Section — cycling scroll list
// Active item is full opacity; the next item peeks below at 0.3 opacity.
// Auto-advances on a configurable interval and loops infinitely. Hovering
// the module pauses autoplay, locks page scroll, and lets the mouse wheel
// step through items one at a time (either direction).
// =============================================================================

( function () {
	'use strict';

	function initBenefits( viewport ) {
		var track = viewport.querySelector( '.bn__scroll-track' );
		if ( ! track ) return;

		var originals = Array.from( track.children );
		var n = originals.length;
		if ( n < 1 ) return;

		var delay  = Math.max( 500, parseInt( viewport.dataset.delay, 10 ) || 4000 );
		var ANIM   = 650; // transition duration, ms
		var PEEK   = 100; // px of next item to show below the active item

		// Clone a copy before AND after the originals, so the track can wrap
		// seamlessly in either direction (wheel scrolling needs to go backward
		// too, not just forward like the old autoplay-only version did).
		originals.forEach( function ( item ) {
			track.appendChild( item.cloneNode( true ) );
		} );
		originals.slice().reverse().forEach( function ( item ) {
			track.insertBefore( item.cloneNode( true ), track.firstChild );
		} );

		var all    = Array.from( track.children ); // [clones before][originals][clones after], length 3n
		var domIdx = n; // start on the first original item, in the middle copy
		var locked = false;

		// Sum of offsetHeights of items 0…i-1
		function offsetFor( i ) {
			var s = 0;
			for ( var j = 0; j < i; j++ ) {
				s += all[ j ].offsetHeight;
			}
			return s;
		}

		// Set viewport height = tallest item + peek; fixed until resize
		function setHeight() {
			var maxH = Math.max.apply( null, originals.map( function( item ) { return item.offsetHeight; } ) );
			viewport.style.height = ( maxH + PEEK ) + 'px';
		}

		// Move the track to domIdx, with or without a transition
		function moveTrack( animate ) {
			track.style.transition = animate
				? 'transform ' + ANIM + 'ms cubic-bezier(0.4,0,0.2,1)'
				: 'none';
			track.style.transform = 'translateY(-' + offsetFor( domIdx ) + 'px)';
		}

		// Apply is-active to originals and both sets of clones
		function updateActive() {
			var logIdx = domIdx % n;
			all.forEach( function ( item, i ) {
				item.classList.toggle( 'is-active', i % n === logIdx );
			} );
		}

		// dir = 1 to advance forward, -1 to go backward
		function step( dir ) {
			if ( locked ) return;
			locked = true;

			domIdx += dir;
			updateActive();
			moveTrack( true );
			setHeight();

			setTimeout( function () {
				if ( domIdx >= 2 * n ) {
					// Silently snap back from the trailing clone copy into the middle one
					domIdx -= n;
					moveTrack( false );
					updateActive();
					setHeight();
				} else if ( domIdx < n ) {
					// Silently snap forward from the leading clone copy into the middle one
					domIdx += n;
					moveTrack( false );
					updateActive();
					setHeight();
				}
				requestAnimationFrame( function () {
					requestAnimationFrame( function () {
						locked = false;
					} );
				} );
			}, ANIM + 60 );
		}

		function advance() { step( 1 ); }
		function retreat() { step( -1 ); }

		// Initialise
		moveTrack( false );
		updateActive();
		viewport.classList.add( 'is-initialized' );
		requestAnimationFrame( setHeight );

		var autoplayTimer = null;
		function startAutoplay() {
			if ( n > 1 && ! autoplayTimer ) {
				autoplayTimer = setInterval( advance, delay );
			}
		}
		function stopAutoplay() {
			clearInterval( autoplayTimer );
			autoplayTimer = null;
		}
		startAutoplay();

		if ( n > 1 ) {
			// Hovering pauses autoplay and hands control to the wheel
			viewport.addEventListener( 'mouseenter', stopAutoplay );
			viewport.addEventListener( 'mouseleave', startAutoplay );

			// Wheel over the module always locks page scroll (so the page never
			// creeps while someone's mid-gesture over it); a step only fires
			// if the previous one has finished animating.
			viewport.addEventListener( 'wheel', function ( e ) {
				e.preventDefault();
				if ( e.deltaY > 0 ) {
					advance();
				} else if ( e.deltaY < 0 ) {
					retreat();
				}
			}, { passive: false } );
		}

		var resizeTimer;
		window.addEventListener( 'resize', function () {
			clearTimeout( resizeTimer );
			resizeTimer = setTimeout( function () {
				moveTrack( false );
				setHeight();
			}, 150 );
		} );
	}

	document.addEventListener( 'DOMContentLoaded', function () {
		document.querySelectorAll( '.bn__scroll-viewport[data-delay]' ).forEach( initBenefits );
	} );

} )();


// =============================================================================
// Card Slider Section
// Desktop: auto-scrolling track (stops at end) + mouse/touch drag, clamped.
// Mobile:  accordion — click headline to open/close one card at a time.
// =============================================================================

( function () {
	'use strict';

	var DESKTOP_SPEED = 50;   // px/s
	var MOBILE_BP     = 768;  // px

	function initCardSlider( section ) {

		var trackOuter = section.querySelector( '.cs__track-outer' );
		var track      = section.querySelector( '.cs__track' );
		if ( ! trackOuter || ! track ) return;

		var origCards = Array.from( track.querySelectorAll( '.cs__card' ) );
		var n         = origCards.length;
		if ( n === 0 ) return;

		var isMobile  = window.innerWidth < MOBILE_BP;
		var mobileIdx = 0;
		var rafId     = null;


		// ── Shared helper ──────────────────────────────────────────────────────

		function resetCardStyle( card ) {
			// Restore only the PHP-authored inline style (--cs-border); strip JS overrides.
			card.style.cssText = card.getAttribute( 'style' ) || '';
		}


		// ══════════════════════════════════════════════════════════════════════
		// DESKTOP — infinite marquee loop
		// ══════════════════════════════════════════════════════════════════════

		var pos      = 0;
		var lastTs   = null;
		var paused   = false;
		var unitStep = 0;
		var setWidth = 0;   // total width of one original set; loop point

		function buildDesktop() {
			if ( rafId ) { cancelAnimationFrame( rafId ); rafId = null; }
			track.innerHTML     = '';
			track.style.cssText = '';

			origCards.forEach( function ( c ) {
				c.classList.remove( 'is-open' );
				resetCardStyle( c );
				track.appendChild( c );
			} );
			// Append clones for seamless loop
			origCards.forEach( function ( c ) {
				track.appendChild( c.cloneNode( true ) );
			} );
		}

		function measureDesktop() {
			var first = track.firstElementChild;
			if ( ! first ) return;
			var gap  = parseFloat( window.getComputedStyle( track ).columnGap ) || 44;
			unitStep = first.offsetWidth + gap;
			setWidth = unitStep * n;
		}

		function tickDesktop( ts ) {
			if ( ! paused ) {
				if ( lastTs !== null ) {
					pos += DESKTOP_SPEED * ( ts - lastTs ) / 1000;
					if ( pos >= setWidth ) pos -= setWidth;
					track.style.transform = 'translateX(-' + pos.toFixed( 2 ) + 'px)';
				}
				lastTs = ts;
			} else {
				lastTs = null;
			}
			rafId = requestAnimationFrame( tickDesktop );
		}

		// Mouse drag
		var dragStartX   = 0;
		var dragStartPos = 0;
		var isDragging   = false;

		trackOuter.addEventListener( 'mousedown', function ( e ) {
			if ( isMobile ) return;
			isDragging   = true;
			dragStartX   = e.clientX;
			dragStartPos = pos;
			paused       = true;
			e.preventDefault();
		} );
		document.addEventListener( 'mousemove', function ( e ) {
			if ( ! isDragging ) return;
			var delta = dragStartX - e.clientX;
			pos = ( ( dragStartPos + delta ) % setWidth + setWidth ) % setWidth;
			track.style.transform = 'translateX(-' + pos.toFixed( 2 ) + 'px)';
		} );
		document.addEventListener( 'mouseup', function () {
			if ( ! isDragging ) return;
			isDragging = false;
			paused     = false;
		} );

		// Touch drag (desktop/tablet)
		var touchDragStartX = 0;
		trackOuter.addEventListener( 'touchstart', function ( e ) {
			if ( isMobile ) return;
			touchDragStartX = e.touches[ 0 ].clientX;
			dragStartPos    = pos;
			paused          = true;
		}, { passive: true } );
		trackOuter.addEventListener( 'touchmove', function ( e ) {
			if ( isMobile ) return;
			var delta = touchDragStartX - e.touches[ 0 ].clientX;
			pos = ( ( dragStartPos + delta ) % setWidth + setWidth ) % setWidth;
			track.style.transform = 'translateX(-' + pos.toFixed( 2 ) + 'px)';
		}, { passive: true } );
		trackOuter.addEventListener( 'touchend', function () {
			if ( isMobile ) return;
			paused = false;
		}, { passive: true } );

		// Hover pause/resume
		trackOuter.addEventListener( 'mouseenter', function () { if ( ! isMobile ) { paused = true; } } );
		trackOuter.addEventListener( 'mouseleave', function () { if ( ! isMobile ) { paused = false; } } );


		// ══════════════════════════════════════════════════════════════════════
		// MOBILE — accordion
		// ══════════════════════════════════════════════════════════════════════

		function buildMobile() {
			if ( rafId ) { cancelAnimationFrame( rafId ); rafId = null; }
			track.innerHTML     = '';
			track.style.cssText = '';

			origCards.forEach( function ( c ) {
				c.classList.remove( 'is-open' );
				resetCardStyle( c );
				track.appendChild( c );
			} );

			// Open the tracked card (first on init)
			origCards[ mobileIdx ].classList.add( 'is-open' );
		}

		// Click handlers — attached once; guard on isMobile at runtime
		origCards.forEach( function ( card, i ) {
			var hl = card.querySelector( '.cs__card-headline' );
			if ( ! hl ) return;
			hl.addEventListener( 'click', function () {
				if ( ! isMobile ) return;
				if ( card.classList.contains( 'is-open' ) ) return;
				origCards.forEach( function ( c ) { c.classList.remove( 'is-open' ); } );
				card.classList.add( 'is-open' );
				mobileIdx = i;
			} );
		} );


		// ══════════════════════════════════════════════════════════════════════
		// RESIZE — reinit on breakpoint crossing
		// ══════════════════════════════════════════════════════════════════════

		var resizeTimer = null;
		window.addEventListener( 'resize', function () {
			clearTimeout( resizeTimer );
			resizeTimer = setTimeout( function () {
				var nowMobile = window.innerWidth < MOBILE_BP;
				if ( nowMobile !== isMobile ) {
					isMobile = nowMobile;
					pos      = 0;
					lastTs   = null;
					if ( isMobile ) {
						buildMobile();
					} else {
						buildDesktop();
						measureDesktop();
						rafId = requestAnimationFrame( tickDesktop );
					}
				} else if ( ! isMobile ) {
					measureDesktop();
				}
			}, 150 );
		} );


		// ══════════════════════════════════════════════════════════════════════
		// INIT
		// ══════════════════════════════════════════════════════════════════════

		if ( isMobile ) {
			buildMobile();
		} else {
			buildDesktop();
			measureDesktop();
			rafId = requestAnimationFrame( tickDesktop );
		}
	}

	document.addEventListener( 'DOMContentLoaded', function () {
		document.querySelectorAll( '.rmc-card-slider-section' ).forEach( initCardSlider );
	} );

} )();


// =============================================================================
// COLLAPSIBLE PANELS — single-open accordion
// =============================================================================

;( function () {

	function initAccordion( accordion ) {
		var panels = Array.prototype.slice.call( accordion.querySelectorAll( '.cp__panel' ) );
		if ( ! panels.length ) return;

		panels.forEach( function ( panel ) {
			var btn = panel.querySelector( '.cp__bar' );
			if ( ! btn ) return;
			btn.addEventListener( 'click', function () {
				var isOpen = panel.classList.contains( 'is-open' );
				panels.forEach( closePanel );
				if ( ! isOpen ) openPanel( panel );
			} );
		} );
	}

	function openPanel( panel ) {
		panel.classList.add( 'is-open' );
		var btn     = panel.querySelector( '.cp__bar' );
		var content = panel.querySelector( '.cp__content' );
		if ( btn )     btn.setAttribute( 'aria-expanded', 'true' );
		if ( content ) content.setAttribute( 'aria-hidden', 'false' );
	}

	function closePanel( panel ) {
		panel.classList.remove( 'is-open' );
		var btn     = panel.querySelector( '.cp__bar' );
		var content = panel.querySelector( '.cp__content' );
		if ( btn )     btn.setAttribute( 'aria-expanded', 'false' );
		if ( content ) content.setAttribute( 'aria-hidden', 'true' );
	}

	document.addEventListener( 'DOMContentLoaded', function () {
		document.querySelectorAll( '.cp__accordion' ).forEach( initAccordion );
	} );

} )();


// =============================================================================
// SEARCH PAGE — live results (page-templates/search.php)
// Typing (debounced), the All/Pages/Blog Posts toggles and the sort dropdown
// all refresh results in place: the same page is fetched with the new query
// string and its #rmc-search-results block swapped in. The URL is kept in sync
// so results stay shareable. Without JS the form still works as a plain GET.
// =============================================================================

( function () {
	'use strict';

	var DEBOUNCE_MS = 300;
	var DEFAULTS    = { q: '', type: 'all', sort: 'relevance' };

	function init( form ) {
		var input    = form.querySelector( '.rmc-search__input' );
		var clearBtn = form.querySelector( '.rmc-search__clear-input' );
		var results  = document.getElementById( 'rmc-search-results' );
		if ( ! input || ! results ) return;

		var timer      = null;
		var controller = null;

		function buildUrl() {
			var url  = new URL( form.action, window.location.href );
			var data = new FormData( form );
			Object.keys( DEFAULTS ).forEach( function ( key ) {
				var value = ( data.get( key ) || '' ).toString().trim();
				if ( value && value !== DEFAULTS[ key ] ) {
					url.searchParams.set( key, value );
				}
			} );
			return url.toString(); // always page 1 for a new query
		}

		function syncClearButton() {
			if ( clearBtn ) clearBtn.hidden = input.value === '';
		}

		function update() {
			clearTimeout( timer );
			var url = buildUrl();

			if ( controller ) controller.abort();
			controller = 'AbortController' in window ? new AbortController() : null;

			results.setAttribute( 'aria-busy', 'true' );

			fetch( url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined } )
				.then( function ( res ) {
					if ( ! res.ok ) throw new Error( 'HTTP ' + res.status );
					return res.text();
				} )
				.then( function ( html ) {
					var doc  = new DOMParser().parseFromString( html, 'text/html' );
					var next = doc.getElementById( 'rmc-search-results' );
					if ( ! next ) throw new Error( 'No results block' );
					results.innerHTML = next.innerHTML;
					results.setAttribute( 'aria-busy', 'false' );
					window.history.replaceState( null, '', url );
				} )
				.catch( function ( err ) {
					if ( err && err.name === 'AbortError' ) return;
					window.location.href = url; // fall back to a normal page load
				} );
		}

		function clearSearch() {
			input.value = '';
			var all = form.querySelector( 'input[name="type"][value="' + DEFAULTS.type + '"]' );
			if ( all ) all.checked = true;
			syncClearButton();
			update();
			input.focus();
		}

		input.addEventListener( 'input', function () {
			syncClearButton();
			clearTimeout( timer );
			timer = setTimeout( update, DEBOUNCE_MS );
		} );

		form.addEventListener( 'change', function ( e ) {
			if ( e.target.name === 'type' || e.target.name === 'sort' ) update();
		} );

		form.addEventListener( 'submit', function ( e ) {
			e.preventDefault();
			update();
		} );

		if ( clearBtn ) {
			clearBtn.addEventListener( 'click', function () {
				input.value = '';
				syncClearButton();
				update();
				input.focus();
			} );
		}

		// "Clear search" lives inside the results block, which gets replaced —
		// listen on the container instead of the link.
		results.addEventListener( 'click', function ( e ) {
			var link = e.target.closest( '[data-rmc-search-clear]' );
			if ( ! link ) return;
			e.preventDefault();
			clearSearch();
		} );

		syncClearButton();
	}

	document.addEventListener( 'DOMContentLoaded', function () {
		document.querySelectorAll( 'form[data-rmc-search]' ).forEach( init );
	} );

} )();
